/**
 * React Query bindings for the generated REST client.
 *
 * Hook signatures intentionally mirror the legacy `trpc.x.y.useQuery()` shape
 * so the per-page tRPC sweep is mechanical:
 *
 *   trpc.dashboard.subjects.useQuery()
 *   →
 *   useApiQuery("get", "/dashboard/subjects")
 *
 *   trpc.lessons.complete.useMutation()
 *   →
 *   useApiMutation("post", "/topics/{topicId}/lesson/complete")
 *
 *   utils.fsrs.dueCount.invalidate()
 *   →
 *   queryClient.invalidateQueries({ queryKey: ["get", "/fsrs/due-count"] })
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  ClientPathsWithMethod,
  MaybeOptionalInit,
  MethodResponse,
} from "openapi-fetch";
import { api } from "./client";
import type { paths } from "./schema";

// Retrying 4xx is pointless and dangerous: a stale/invalid token won't fix
// itself by being sent again, and at scale the storm hammers Clerk's JWKS
// endpoint until it rate-limits us. Bail immediately on auth-class errors;
// rely on default exponential backoff (3 retries) for transient 5xx / network.
function shouldRetry(failureCount: number, error: unknown) {
  const status = (error as { status?: number } | null)?.status;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 3;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: shouldRetry,
    },
    mutations: {
      retry: shouldRetry,
    },
  },
});

type ApiClient = typeof api;

type GetPath = ClientPathsWithMethod<ApiClient, "get">;

// ─────────────────────────── useApiQuery ──────────────────────────────────

export function useApiQuery<P extends GetPath>(
  method: "get",
  path: P,
  init?: MaybeOptionalInit<paths[P], "get">,
  options?: Omit<
    UseQueryOptions<MethodResponse<ApiClient, "get", P>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<MethodResponse<ApiClient, "get", P>, Error> {
  return useQuery<MethodResponse<ApiClient, "get", P>, Error>({
    queryKey: [method, path, init],
    queryFn: async () => {
      const { data, error } = await api.GET(path as never, init as never);
      if (error) throw error as Error;
      return data as MethodResponse<ApiClient, "get", P>;
    },
    ...options,
  });
}

// ────────────────────────── useApiMutation ────────────────────────────────

type WriteMethod = "post" | "patch" | "put" | "delete";

type WriteInit<
  M extends WriteMethod,
  P extends ClientPathsWithMethod<ApiClient, M>,
> = MaybeOptionalInit<paths[P], M>;

export function useApiMutation<
  M extends WriteMethod,
  P extends ClientPathsWithMethod<ApiClient, M>,
>(
  method: M,
  path: P,
  options?: Omit<
    UseMutationOptions<MethodResponse<ApiClient, M, P>, Error, WriteInit<M, P>>,
    "mutationKey" | "mutationFn"
  >,
): UseMutationResult<MethodResponse<ApiClient, M, P>, Error, WriteInit<M, P>> {
  return useMutation<MethodResponse<ApiClient, M, P>, Error, WriteInit<M, P>>({
    mutationKey: [method, path],
    mutationFn: async (init) => {
      const { data, error } = await api.request(
        method as never,
        path as never,
        init as never,
      );
      if (error) throw error as Error;
      return data as MethodResponse<ApiClient, M, P>;
    },
    ...options,
  });
}
